import { useState, useEffect, useRef, useCallback } from "react";

import { ScheduleItem, getChannelSchedule } from "../data/creatorsChoiceData"; // Keep generic types
// supabase import removed (unused)

// DB Track Type removed (unused)

interface PseudoLiveState {
  currentTrack: ScheduleItem | null;
  nextTrack: ScheduleItem | null;
  startOffset: number;
  isLoading: boolean;
  error: string | null;
  timeUntilNext: number; // Added missing prop
}

export const usePseudoLive = (channelId: string | null) => {
  const [state, setState] = useState<PseudoLiveState>({
    currentTrack: null,
    nextTrack: null,
    startOffset: 0,
    isLoading: true,
    error: null,
    timeUntilNext: 0,
  });

  const transitionTimerRef = useRef<NodeJS.Timeout | null>(null);
  const syncSchedulerRef = useRef<() => void>(() => {});

  // 1. Fetch Tracks for Channel - DISABLED (Using static schedule for now)
  // Logic removed to fix unused vars.

  // 3. Smart Mixer Logic (The "Virtual DJ")
  const generateSmartSchedule = useCallback(() => {
    // Use the manual schedule definition from creatorsChoiceData which is now the Source of Truth
    // This supports the custom Pod -> Jingle -> Song interleaving requested.
    const schedule = getChannelSchedule(channelId || "default");
    return schedule;
  }, [channelId]);

  // 4. Time Sync (The "Clock")
  const syncScheduler = useCallback(() => {
    const schedule = generateSmartSchedule();
    if (schedule.length === 0) return;

    // Calculate Loop Duration accounting for Overlaps (Crossfades)
    // Duration of Cycle = Sum(ItemDuration) - Sum(Overlap with Next)
    let totalDuration = 0;
    schedule.forEach((item, _i) => {
      const duration = item.duration || item.track.duration;
      const overlap = item.crossfadeDuration || 0;
      // The amount of "exclusive usage" time this track adds to the timeline is (duration - overlap)
      // BUT, the overlap is shared with the NEXT track.
      // Start Time A = 0.
      // Start Time B = Duration A - Overlap A.
      // Start Time C = Start Time B + Duration B - Overlap B.
      // Cycle Length = Start Time Last + Duration Last - Overlap Last (loop to start)
      totalDuration += duration - overlap;
    });

    const now = Date.now() / 1000;
    const loopPosition = now % totalDuration;

    let accumulatedTime = 0;
    let currentItem: ScheduleItem | null = null;
    let nextItem: ScheduleItem | null = null;
    let startOffset = 0;

    for (let i = 0; i < schedule.length; i++) {
      const item = schedule[i];
      const itemDuration = item.duration || item.track.duration;
      const overlap = item.crossfadeDuration || 0;

      // Item Start Time in the Loop = accumulatedTime
      // Item End Time in the Loop = accumulatedTime + itemDuration
      const itemUnwrappedStart = accumulatedTime;
      const itemUnwrappedEnd = accumulatedTime + itemDuration;

      // Check if we are "in" this item
      // Note: because of overlaps, we might be in TWO items at once.
      // But we designate the "primary" item as the one whose "exclusive" window is active?
      // Or just the one that started most recently and hasn't finished?
      // Actually, playing "Item A" usually means "Main A is active".
      // If we are in the crossfade zone (A ending, B starting), A is still "Current".
      // B is "Next" (and playing).

      // Loop Position Check:
      // If we are before the start? No.
      // If we are after the end? No.
      if (
        loopPosition >= itemUnwrappedStart &&
        loopPosition < itemUnwrappedEnd
      ) {
        // Found current item
        const playOffset = loopPosition - itemUnwrappedStart;

        currentItem = {
          ...item,
          startTime: now - playOffset,
          endTime: now + (itemDuration - playOffset),
          sourceOffset: (item.sourceOffset || 0) + playOffset,
          // Pass crossfade info
          crossfadeDuration: overlap,
        };

        // PREPARE NEXT ITEM (Critical for Crossfade)
        // Next item starts `overlap` seconds BEFORE current item ends.
        const nextIndex = (i + 1) % schedule.length;
        const next = schedule[nextIndex];

        // Next item start time relative to NOW:
        // It should start at `currentItem.endTime - currentItem.crossfadeDuration`
        const nextStartTime = currentItem.endTime - overlap;

        // But wait, if we are ALREADY in the crossfade zone (playOffset > duration - overlap),
        // then Next Item SHOULD BE PLAYING already.
        // The Engine handles the "When to start playing".
        // usePseudoLive just reports "Who is next".

        nextItem = {
          ...next,
          startTime: nextStartTime,
          endTime: nextStartTime + (next.duration || next.track.duration),
          sourceOffset: next.sourceOffset || 0,
        };

        startOffset = currentItem.sourceOffset || 0;
        break;
      }

      // Advance accumulated time by the "non-overlapped" portion (to start of next)
      // Next Start = Current Start + Duration - Overlap
      accumulatedTime += itemDuration - overlap;
    }

    if (currentItem && nextItem) {
      setState({
        currentTrack: currentItem,
        nextTrack: nextItem,
        startOffset: startOffset,
        isLoading: false,
        error: null,
        timeUntilNext: currentItem.endTime - now,
      });

      // Schedule Next Sync
      if (transitionTimerRef.current) clearTimeout(transitionTimerRef.current);
      transitionTimerRef.current = setTimeout(
        () => {
          // Recurse via Ref to avoid "access before declaration" lint
          syncSchedulerRef.current?.();
        },
        (currentItem.endTime - now) * 1000 + 500,
      ); // Sync after end
    }
  }, [generateSmartSchedule]);

  // Keep ref updated
  useEffect(() => {
    syncSchedulerRef.current = syncScheduler;
  }, [syncScheduler]);

  // Trigger sync when ready
  // Trigger sync when ready - Async to avoid cascading render warning
  useEffect(() => {
    const t = setTimeout(() => {
      syncScheduler();
    }, 0);
    return () => clearTimeout(t);
  }, [syncScheduler]);

  // Resync on focus
  useEffect(() => {
    const handleFocus = () => syncScheduler();
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [syncScheduler]);

  return { ...state, forceSync: syncScheduler };
};
